/**
 * InfrastructureTabbedPanel - Panel izquierdo con pestañas para módulo de infraestructura
 *
 * @version 1.0
 * @module core/modules/infrastructure
 *
 * @description
 * Pestañas: Luminarias, Semáforos, Mobiliario, Pavimentos, Redes, Drenaje
 */

import { useState } from 'react';
import {
  Lightbulb, TrafficCone, Armchair, Construction, Cable, Waves,
} from 'lucide-react';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useInfrastructureElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { InfrastructureElementId } from './constants/element-colors';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type InfraTabType =
  | 'lighting'
  | 'traffic-lights'
  | 'furniture'
  | 'pavement'
  | 'networks'
  | 'drainage';

interface TabInfo {
  id: InfraTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'lighting',       icon: <Lightbulb size={16} />,    tooltip: 'Alumbrado público', title: 'Luminarias',  emptyMessage: 'Sin luminarias registradas' },
  { id: 'traffic-lights', icon: <TrafficCone size={16} />,  tooltip: 'Semáforos',         title: 'Semáforos',   emptyMessage: 'Sin semáforos registrados' },
  { id: 'furniture',      icon: <Armchair size={16} />,     tooltip: 'Mobiliario urbano', title: 'Mobiliario',  emptyMessage: 'Sin mobiliario registrado' },
  { id: 'pavement',       icon: <Construction size={16} />, tooltip: 'Pavimentos',        title: 'Pavimentos',  emptyMessage: 'Sin pavimentos registrados' },
  { id: 'networks',       icon: <Cable size={16} />,        tooltip: 'Redes',             title: 'Redes',       emptyMessage: 'Sin redes registradas' },
  { id: 'drainage',       icon: <Waves size={16} />,        tooltip: 'Drenaje pluvial',   title: 'Drenaje',     emptyMessage: 'Sin drenaje registrado' },
];

export function InfrastructureTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<InfraTabType>('lighting');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useInfrastructureElementColorsStore((s) => s.getColor);
  const setColor = useInfrastructureElementColorsStore((s) => s.setColor);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `infra-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  /**
   * Maneja el clic en el código de un elemento
   *
   * @remarks
   * Antes de cambiar de elemento, verifica si hay cambios sin guardar en el panel derecho.
   * Despacha un evento personalizado que puede ser manejado por el módulo.
   */
  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log(`[InfrastructureTabbedPanel] Elemento clickeado: ${activeTab} - ${elementId}`);

    // Verificar cambios sin guardar
    const canChange = await canChangeContent();
    if (!canChange) {
      console.warn('[InfrastructureTabbedPanel] Usuario canceló el cambio');
      return;
    }

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[InfrastructureTabbedPanel] Despachando evento: infra-${activeTab}-select`);
    window.dispatchEvent(new CustomEvent(`infra-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            <BaseInventoryTab
              key={activeTab}
              title={activeTabInfo.title}
              emptyMessage={activeTabInfo.emptyMessage}
              eventPrefix={`infra-${activeTab}`}
              moduleId="infrastructure"
              elementColor={isMappable ? getColor(activeTab as InfrastructureElementId) : undefined}
              onElementColorChange={isMappable ? (hex) => setColor(activeTab as InfrastructureElementId, hex) : undefined}
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
